import FETimeUtils from '@spothero/utils/time';
import DateUtils from 'utils/date';
import dayjs from 'utils/dayjs-timezone';
import dayjsduration from 'utils/dayjs-duration';

dayjs.extend(dayjsduration);

/**
 * Utilities for working with times.
 *
 * @module TimeUtils
 */
const TimeUtils = {
    /**
     * Check if time is more than 30 minutes in the past and return updated time if not.
     *
     * @static
     * @function checkStaleStart
     * @param {object} starts - The start date/time as a moment instance.
     * @param {string} currentCityTimezone - The target city timezone string value i.e. "America/New_York".
     * @example
     * const {
     *     newStart,
     *     isNewStart
     * } = TimeUtils.checkStaleStart(moment(), 'America/New_York');
     * @returns {object} - Returns the newStart (a moment instance) and a boolean isNewStart.
     */
    checkStaleStart(starts, currentCityTimezone) {
        let newStart = starts;
        let isNewStart = false;
        let newCurrentCityTimezone = currentCityTimezone;

        if (!newCurrentCityTimezone) {
            newCurrentCityTimezone = DateUtils.getTimeZone();
        }

        // create moment if not already a moment
        if (!dayjs.isDayjs(newStart)) {
            newStart = dayjs(starts);
        }

        // MR(Legacy note carried over) - bk (7/2016) okay this shit is crazy but here is what happens
        // - both nowLocalized and newStart are both timezone unaware moment objects
        // - when we add the tz newCurrentCityTimezone to nowLocalized it resolves to the same time in the new timezone
        // - when we add the tz newCurrentCityTimezone to newStart it resolves to the timezone adjusted time in the new timezone
        // - in order to normalize the two and maintain the proper timezone i subtract the difference in minutes between the system and newCurrentCityTimezone timezones
        // - we then determine if the request start is before the local current time
        // - if the time is stale we then determine if the local time is before or after the 30 minute mark to determine if we should round up or down to the nearest half hour to ensure its inclusive of the current time
        let nowLocalized = dayjs().tz(currentCityTimezone);
        const startDateLocalized = newStart
            .tz(newCurrentCityTimezone)
            .add(nowLocalized.utcOffset() - newStart.utcOffset(), 'minutes');

        nowLocalized = nowLocalized.tz(newCurrentCityTimezone);

        if (
            startDateLocalized.isBefore(
                nowLocalized.clone().subtract(30, 'minutes')
            )
        ) {
            const nowMinute = nowLocalized.minute();

            if (nowMinute > 30) {
                newStart = nowLocalized.add((30 - nowMinute) % 30, 'minutes');
            } else {
                newStart = nowLocalized.subtract(nowMinute, 'minutes');
            }

            isNewStart = true;
        }

        return {
            isNewStart,
            newStart,
        };
    },

    /**
     * Outputs SpotHero standard formatted time duration string from milliseconds.
     *
     * @param {object} duration - Difference between two times in milliseconds
     * @param {object} hoursOnly - Returns a string with hour total only
     * @example
     * TimeUtils.prettyDuration(100000);
     * TimeUtils.prettyDuration(momentEndDate.diff(momentStartDate));
     * @returns {string} SpotHero standard formatted time duration string (i.e. "3 days, 12 hours, 30 minutes")
     */
    prettyDuration(duration) {
        return dayjs
            .duration(duration)
            .format('D [days], H [hours], m [minutes]')
            .replace(/\b0\b days, /, '')
            .replace(', 0 hours', '')
            .replace('0 hours, ', '')
            .replace(', 0 minutes', '')
            .replace('0 minutes', '')
            .replace('undefined days', '')
            .replace(', undefined hours', '')
            .replace(', undefined minutes', '')
            .replace('$1 days', '1 day')
            .replace('$1 hours', '1 hour')
            .replace('$1 minutes', '1 minute');
    },

    /**
     * Returns whether or not time has been extended, as well as the number of hours extended.
     *
     * @static
     * @function getTimeExtension
     * @param {object} data - The data to pass to the function.
     * @param {object} data.requestedStartDateTimeObj - The requested start date as a moment or dayjs instance.
     * @param {object} data.requestedEndDateTimeObj - The requested end date as a moment or dayjs instance.
     * @param {object} data.actualStartDateTimeObj - The actual start date as a moment or dayjs instance.
     * @param {object} data.actualEndDateTimeObj - The actual start date as a moment or dayjs instance.
     * @param {boolean} data.isMonthly - Is startDateTimeObj a monthly datetime?
     * @example
     * TimeUtils.getTimeExtension({
     *     requestedStartDateTimeObj: moment(),
     *     requestedEndDateTimeObj: moment().add(7, 'hours').add(23, 'minutes'),
     *     actualStartDateTimeObj: moment(),
     *     actualEndDateTimeObj: moment().add(9, 'hours').add(23, 'minutes'),
     *     isMonthly: false,
     * });
     * @returns {object} - This contains isExtended (whether or not time has been extended), and hoursExtended (the number of hours extended)
     */
    getTimeExtension({
        requestedStartDateTimeObj,
        requestedEndDateTimeObj,
        actualStartDateTimeObj,
        actualEndDateTimeObj,
        isMonthly,
    }) {
        if (isMonthly) {
            return {
                isExtended: false,
                formattedDuration: null,
            };
        } else {
            const isExtended =
                requestedStartDateTimeObj.isAfter(actualStartDateTimeObj) ||
                actualEndDateTimeObj.isAfter(requestedEndDateTimeObj);
            const startsDuration = dayjs.duration(
                requestedStartDateTimeObj.diff(actualStartDateTimeObj)
            );
            const endsDuration = dayjs.duration(
                actualEndDateTimeObj.diff(requestedEndDateTimeObj)
            );
            const formattedDuration = this.prettyDuration(
                startsDuration.add(endsDuration)
            );

            return {
                isExtended,
                formattedDuration,
            };
        }
    },
};

export default {
    ...FETimeUtils,
    ...TimeUtils,
};
